/**
 * Generated-content merge tags: the per-brand copy the onboarding pipeline
 * writes into the `generated_content` data source (headline, subheaders, body
 * copy, bullets, CTAs). In an editor these resolve through a GrapesJS
 * `data-variable` bound to `generated_content.data.<key>`, so the canvas and
 * the exported HTML both show the brand's real copy rather than DTL.
 *
 * This module is the single list of keys the product grid may bind to. The
 * onboarding `contentFieldDefinitions` must stay in step with it (a test
 * guards the drift), because that list is what seeds the data source.
 */

export const GENERATED_CONTENT_PREFIX = "generated_content.data.";

export interface GeneratedContentField {
  /** Record key inside the `generated_content` data source. */
  key: string;
  /** Human label, as shown on chips and in the picker. */
  label: string;
}

/** Ticket order: headline, subheaders, body copy, bullets, CTAs. */
export const GENERATED_CONTENT_FIELDS: readonly GeneratedContentField[] = [
  { key: "headline", label: "Headline" },
  { key: "subheader_1", label: "Subheader 1" },
  { key: "subheader_2", label: "Subheader 2" },
  { key: "subheader_3", label: "Subheader 3" },
  { key: "body_copy_1", label: "Body copy 1" },
  { key: "body_copy_2", label: "Body copy 2" },
  { key: "bullet_point_1", label: "Bullet point 1" },
  { key: "bullet_point_2", label: "Bullet point 2" },
  { key: "bullet_point_3", label: "Bullet point 3" },
  { key: "call_to_action_1", label: "Call to action 1" },
  { key: "call_to_action_2", label: "Call to action 2" },
  { key: "call_to_action_3", label: "Call to action 3" },
];

const FIELD_BY_KEY: ReadonlyMap<string, GeneratedContentField> = new Map(
  GENERATED_CONTENT_FIELDS.map((field) => [field.key, field]),
);

/** `generated_content.data.<key>`: the merge-tag identifier and the data-variable path. */
export function generatedContentIdentifier(key: string): string {
  return `${GENERATED_CONTENT_PREFIX}${key}`;
}

/**
 * The key behind a `generated_content.data.<key>` identifier, or undefined
 * when the identifier is anything else. Only the known keys are accepted, so
 * a typo like `generated_content.data.headlne` stays a literal string instead
 * of becoming a binding that silently renders empty.
 */
export function parseGeneratedContentIdentifier(
  identifier: string,
): string | undefined {
  const trimmed = identifier.trim();
  if (!trimmed.startsWith(GENERATED_CONTENT_PREFIX)) return undefined;
  const key = trimmed.slice(GENERATED_CONTENT_PREFIX.length);
  return FIELD_BY_KEY.has(key) ? key : undefined;
}

/** The field definition for a known key, or undefined. */
export function getGeneratedContentField(
  key: string,
): GeneratedContentField | undefined {
  return FIELD_BY_KEY.get(key);
}
